import React, {
  type CSSProperties,
  useMemo,
  useLayoutEffect,
  useState,
} from 'react';

import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import styledC, { type CSSObject } from 'styled-components';
import {
  space,
  type SpaceProps,
  color,
  type ColorProps,
  typography as typographySystem,
  type TypographyProps,
  system,
} from 'styled-system';

import { useForwardedRef } from '@adsk/alloy-react-helpers';
import {
  flattenStyle,
  type StylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';

import {
  addTransientPropTypes,
  getTextTypography,
  processTextContent,
  shouldForwardProp,
  transient,
  type WithTransientProps,
} from '../helpers';
import { useBoxTheme } from '../hooks/useBoxTheme';
import {
  type Colors,
  type Typography,
  propTypes as themePropTypes,
} from '../Theme';
import { CSSPropertiesStyleProp } from '../type';

export const textPropTypes = addTransientPropTypes({
  ...propTypes.space,
  ...propTypes.color,
  ...propTypes.typography,
  ...themePropTypes.color,
  ...themePropTypes.typography,
  detectUrls: PropTypes.bool,
  showUrlsIcons: PropTypes.bool,
});

textPropTypes.style = stylePropType;

export interface ITextBaseProps
  extends
    SpaceProps,
    TypographyProps,
    ColorProps,
    Omit<StylableComponent<HTMLSpanElement>, 'style'>,
    Pick<CSSProperties, 'textTransform' | 'whiteSpace' | 'wordBreak'> {
  typography?: Typography;
  color?: Colors;
  bg?: Colors;
  detectUrls?: boolean;
  showUrlsIcons?: boolean;
  style?: CSSPropertiesStyleProp<HTMLSpanElement>;
  /**
   * HTML `dir` attribute for bidirectional text control.
   *
   * When omitted and `children` is a plain string, `dir="auto"` is applied
   * automatically so the browser resolves the base direction from the first
   * strong character. This fixes mixed RTL/LTR content (e.g. "مرحبا John")
   * rendering in wrong word order.
   *
   * Note: any element carrying a `dir` attribute becomes a bidi-isolate
   * (`unicode-bidi: isolate`). For composed `ReactNode` children the
   * automatic default is intentionally skipped to avoid changing bidi
   * grouping for consumers who compose multiple `<Text>` elements on one line.
   * Pass `dir="auto"` explicitly when isolation is needed for non-string children.
   */
  dir?: 'auto' | 'ltr' | 'rtl';
}

export type ITextProps = WithTransientProps<ITextBaseProps>;

const StyledText = styledC.span.withConfig({
  shouldForwardProp,
})<ITextProps & { $typographyCSS?: CSSObject }>(
  ({ $typographyCSS }) => $typographyCSS,
  transient(space),
  transient(color),
  transient(typographySystem),
  transient(
    system({
      textTransform: true,
      whiteSpace: true,
      wordBreak: true,
    }),
  ),
  ({ style }) => flattenStyle(style),
);

const Text = React.forwardRef<HTMLSpanElement, ITextProps>(
  (
    {
      children,
      detectUrls = true,
      showUrlsIcons = true,
      typography,
      $typography,
      style,
      dir,
      ...props
    },
    forwardedRef,
  ) => {
    const [element, setElement] = useState<HTMLElement | null>(null);
    const elementRef = useForwardedRef(forwardedRef);

    const themed = useBoxTheme(props);

    useLayoutEffect(() => {
      setElement(elementRef.current);
    }, [elementRef]);

    const processedTextContent = useMemo(
      () => processTextContent(children, detectUrls, element, showUrlsIcons),
      [children, detectUrls, element, showUrlsIcons],
    );

    const typographyType = $typography || typography;

    // Resolve typography styles as CSS (not inline style) so they don't
    // override explicit styled-system props like fontWeight, fontSize, etc.
    // CSS ordering in StyledText ensures explicit props take precedence.
    const typographyCSS = useMemo(
      () => themed.typography({ typography: typographyType }) || {},
      [themed, typographyType],
    );

    // Only ellipsis display + user's style prop go into inline style.
    const combinedStyle = useMemo(
      () => getTextTypography(typographyType, style),
      [typographyType, style],
    );

    // DSGN-2655: auto bidi for plain-string children; see ITextBaseProps.dir JSDoc
    const resolvedDir =
      dir ?? (typeof children === 'string' ? 'auto' : undefined);

    return (
      <StyledText
        ref={elementRef}
        style={combinedStyle}
        $typographyCSS={typographyCSS}
        dir={resolvedDir}
        {...props}
      >
        {processedTextContent}
      </StyledText>
    );
  },
);

Text.displayName = 'Text';
Text.propTypes = textPropTypes;

export default Text;
